/**
 * Axios instance factory for the new api/ service layer.
 * Auth logic lives in authInterceptor.js — this file only creates instances.
 */
import axios from "axios";
import env from "../../config/env";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import {
  applyAuthInterceptors,
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
  parseCookies,
} from "../authInterceptor";
import { applyHealthInterceptors } from "../../utils/healthReporter";

// --- Timeout config ---
// Global default is 60s (safety net). These are for explicit overrides.
export const API_TIMEOUTS = {
  default: 60000,   // 60s — sync and general calls (server can be slow)
  payment: 120000,  // 120s — payment processing (EDC terminal, external gateways)
  search: 10000,    // 10s — product search (user-facing, should be fast)
  auth: 15000,      // 15s — login/token refresh
};

// --- Axios instances ---

const retailInstance = axios.create({
  baseURL: env.serverUrl,
  method: "POST",
  headers: { "Content-Type": "application/json" },
  timeout: API_TIMEOUTS.default,
});

const genericInstance = axios.create({
  baseURL: env.genericUrl,
  method: "POST",
  headers: { "Content-Type": "application/json" },
  timeout: API_TIMEOUTS.default,
});

const signinInstance = axios.create({
  baseURL: env.signInUrl,
  method: "POST",
  timeout: API_TIMEOUTS.auth,
  headers: {
    "Content-Type": "application/json",
    app_code: "CW-POS",
    app_version: env.releaseNo,
  },
});

// --- Dynamic tenant baseURL interceptors ---
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

genericInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_genericUrl;
  if (url) config.baseURL = url;
  return config;
});

signinInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_signInUrl;
  if (url) config.baseURL = url;
  return config;
});

// --- Auth interceptors (from central module) ---
applyAuthInterceptors(retailInstance);
applyAuthInterceptors(genericInstance);

// signinInstance: attach token only (no 401 retry — it IS the auth service)
signinInstance.interceptors.request.use((config) => {
  const authHeaders = getOAuthHeaders() || {};
  if (authHeaders.access_token) {
    config.headers.Authorization = authHeaders.access_token;
  }
  return config;
});
signinInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// --- Health tracking interceptors (latency + success/failure) ---
applyHealthInterceptors(retailInstance);
applyHealthInterceptors(genericInstance);
applyHealthInterceptors(signinInstance);

export {
  retailInstance,
  genericInstance,
  signinInstance,
  getAccessToken,
  isTokenExpired,
  refreshAccessToken,
  parseCookies,
};
